export type FeatureSettings = {
    fontSize: number;
    textSpacing: boolean;
    highContrast: boolean;
    grayscale: boolean;
    invertColors: boolean;
    highlightLinks: boolean;
    largeCursor: boolean;
    reduceMotion: boolean;
    keyboardNavigation: boolean;
    focusIndicators: boolean;
    readingMode: boolean;
    skipToContent: boolean;
};

export type Profile = {
    slug: string;
    name: string;
    description?: string;
    settings: FeatureSettings;
};

export type WidgetPosition =
    | 'bottom-right'
    | 'bottom-left'
    | 'top-right'
    | 'top-left';

export const widgetPositions: WidgetPosition[] = [
    'bottom-right',
    'bottom-left',
    'top-right',
    'top-left',
];

export function normalizePosition(value: unknown): WidgetPosition {
    return widgetPositions.includes(value as WidgetPosition)
        ? (value as WidgetPosition)
        : 'bottom-right';
}

export { DEFAULT_COLOR, normalizeColor } from './widget/theme';

export type InclusiveOptions = {
    applicationId: string;
    position?: WidgetPosition;
    color?: string;
    colorCode?: string;
    themeColor?: string;
    language?: string;
    apiBaseUrl?: string;
};

export type VoiceEngine = 'auto' | 'sunbird' | 'azure' | 'google' | 'browser';

export type InclusiveState = {
    options: InclusiveOptions;
    position: WidgetPosition;
    color: string;
    language: string;
    profile: string;
    settings: FeatureSettings;
    open: boolean;
    reading: boolean;
    sessionId: string;
    languageServiceAvailable: boolean;
    azureTtsAvailable: boolean;
    googleTtsAvailable: boolean;
    languages: Array<{ code: string; label: string }>;
    translationStatus: TranslationStatus;
    voiceEngine: VoiceEngine;
    browserVoiceURI: string | null;
    /** Playback/synthesis rate. Default 0.85 for clearer, more natural pacing. */
    readingRate: number;
};

export const DEFAULT_READING_RATE = 0.85;
export const MIN_READING_RATE = 0.6;
export const MAX_READING_RATE = 1.15;

export function normalizeReadingRate(value: unknown): number {
    const numeric = typeof value === 'number' ? value : Number(value);

    if (!Number.isFinite(numeric)) {
        return DEFAULT_READING_RATE;
    }

    return Math.min(
        MAX_READING_RATE,
        Math.max(MIN_READING_RATE, Math.round(numeric * 20) / 20),
    );
}

export const voiceEngines: VoiceEngine[] = [
    'auto',
    'sunbird',
    'azure',
    'google',
    'browser',
];

export function normalizeVoiceEngine(value: unknown): VoiceEngine {
    return voiceEngines.includes(value as VoiceEngine)
        ? (value as VoiceEngine)
        : 'auto';
}

export type TranslationStatus =
    | 'idle'
    | 'translating'
    | 'translated'
    | 'unavailable'
    | 'failed';

export const bundledLanguages: Array<{ code: string; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'ach', label: 'Acholi' },
    { code: 'alz', label: 'Alur' },
    { code: 'luc', label: 'Aringa' },
    { code: 'teo', label: 'Ateso' },
    { code: 'bfa', label: 'Bari' },
    { code: 'adh', label: 'Jopadhola' },
    { code: 'keo', label: 'Kakwa' },
    { code: 'kdj', label: 'Karamojong' },
    { code: 'kin', label: 'Kinyarwanda' },
    { code: 'kdi', label: 'Kumam' },
    { code: 'kpz', label: 'Kupsabiny' },
    { code: 'rwm', label: 'Kwamba' },
    { code: 'laj', label: 'Lango' },
    { code: 'lgg', label: 'Lugbara' },
    { code: 'lug', label: 'Luganda' },
    { code: 'rub', label: 'Lugungu' },
    { code: 'gwr', label: 'Lugwere' },
    { code: 'myx', label: 'Lumasaba' },
    { code: 'nuj', label: 'Lunyole' },
    { code: 'xog', label: 'Lusoga' },
    { code: 'mhi', label: "Ma'di" },
    { code: 'pok', label: 'Pokot' },
    { code: 'cgg', label: 'Rukiga' },
    { code: 'koo', label: 'Rukonjo' },
    { code: 'nyn', label: 'Runyankole' },
    { code: 'nyo', label: 'Runyoro' },
    { code: 'ruc', label: 'Ruruuli' },
    { code: 'ttj', label: 'Rutooro' },
    { code: 'lsm', label: 'Samia' },
    { code: 'swa', label: 'Swahili' },
];

export const defaultSettings = (): FeatureSettings => ({
    fontSize: 0,
    textSpacing: false,
    highContrast: false,
    grayscale: false,
    invertColors: false,
    highlightLinks: false,
    largeCursor: false,
    reduceMotion: false,
    keyboardNavigation: false,
    focusIndicators: false,
    readingMode: false,
    skipToContent: true,
});
